import React from 'react'
// libs
import NProgress from 'nprogress'
import { client } from 'lib/apollo-client'
import { Helmet } from 'react-helmet-async'
import { BrowserRouter, useLocation } from 'react-router'
// components
import NetworkStatus from 'components/UI/Warnings/NetworkStatus'
// providers
import CssBaseline from '@mui/material/CssBaseline'
import { ApolloProvider } from '@apollo/client/react'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { ThemeProvider as StyledThemeProvider } from 'styled-components'
// custom providers
import ReduxProvider from 'providers/ReduxProvider'
import SnackbarProvider from 'providers/SnackbarProvider'
import MicrosoftProvider from 'providers/MicrosoftProvider'
import OfflineSyncContainer from 'containers/OfflineSyncContainer'
// theme
import { theme } from 'themes'
import { GlobalStyles } from 'themes/global-styles'
// view components
import { ErrorBoundary } from 'components/ErrorBoundary'
// components
import ModalUrlSync from 'components/ModalUrlSync'
import AppNotifications from 'components/AppNotifications'
// routes
import AppRoutes from './routes'

NProgress.configure({ showSpinner: false })

const RouteChangeListener: React.FC = () => {
  const location = useLocation()

  React.useEffect(() => {
    NProgress.done(true)
  }, [location])

  return null
}

// Last line of defence for everything the page-level boundary in MainLayout does not cover:
// auth pages, the layout itself, modals.
const AppErrorBoundary: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation()

  return (
    <ErrorBoundary source='app' fullScreen resetKey={location.pathname}>
      {children}
    </ErrorBoundary>
  )
}

const App: React.FC = () => {
  return (
    <>
      <Helmet>
        <meta name="theme-color" content={theme.palette.primary.main} />
      </Helmet>
      <ReduxProvider>
        <ApolloProvider client={client}>
          <StyledThemeProvider theme={theme}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <SnackbarProvider>
                    <MicrosoftProvider>
                      <BrowserRouter>
                        <RouteChangeListener />
                        <ModalUrlSync />
                        <CssBaseline />
                        <GlobalStyles />
                        <OfflineSyncContainer />
                        <NetworkStatus />
                        <AppNotifications />
                        <AppErrorBoundary>
                          <AppRoutes />
                        </AppErrorBoundary>
                      </BrowserRouter>
                    </MicrosoftProvider>
                  </SnackbarProvider>
                </LocalizationProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </StyledThemeProvider>
        </ApolloProvider>
      </ReduxProvider>
    </>
  )
}

export default App
